import React, { useState, useRef, useEffect } from 'react';
import { Music, Heart, Briefcase, Users, Building, Utensils, Mail, Phone, MapPin, Send, Star, Award, Globe, Mic, Menu as MenuIcon, X, Volume2, VolumeX, ArrowDown, Play, Pause, CheckCircle2, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../components/ui/sonner';
import { services, highlights, testimonials } from '../mock';
import { ReviewDialog } from '../components/ReviewDialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  heart: Heart,
  briefcase: Briefcase,
  users: Users,
  music: Music,
  building: Building,
  utensils: Utensils,
  mic: Mic
};

export const Home = () => {
  const [submitted, setSubmitted] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const videoRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    venuePostcode: '',
    message: ''
  });

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then((res) => setApprovedReviews(res.data || []))
      .catch(() => setApprovedReviews([]));
  }, []);

  const allTestimonials = [
    ...approvedReviews.map((r) => ({ id: `r-${r.id}`, quote: r.comment, name: r.name, role: r.role || 'Verified Review', rating: r.rating })),
    ...testimonials
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setScrollY]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [approvedReviews]); // Re-observe when async-loaded reviews render new cards

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/bookings`, formData);
      toast.success("Booking Inquiry Sent!", {
        description: "Thank you for your interest. I'll get back to you within 24 hours.",
        duration: 6000,
      });
      setSubmitted(true);
      setTimeout(() => {
        document.querySelector('[data-testid="booking-success-message"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventType: '',
        eventDate: '',
        venuePostcode: '',
        message: ''
      });
    } catch (error) {
      console.error('Booking error:', error);
      const detail = error.response?.data?.detail;
      let description = "Failed to submit booking. Please try again or email info@jlavalymusic.com directly.";
      if (typeof detail === 'string') {
        description = detail;
      } else if (Array.isArray(detail)) {
        description = detail.map((d) => (d?.msg || 'Invalid input').replace(/^Value error, /, '')).join(', ');
      }
      toast.error("Something went wrong", {
        description,
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToBooking = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-500" style={{
        backgroundColor: scrollY > 100 ? 'rgba(0, 0, 0, 0.95)' : 'transparent',
        backdropFilter: scrollY > 100 ? 'blur(10px)' : 'none',
        borderBottom: scrollY > 100 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
      }}>
        <nav className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-3 animate-fade-in hover:opacity-80 transition-opacity duration-300">
              <img 
                src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/0n13s63z_IMG_7492.png" 
                alt="J Lavaly Music" 
                className="h-20 md:h-24 w-auto cursor-pointer"
              />
            </a>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#about" className="nav-link text-white/80 hover:text-white transition-all duration-300 text-sm uppercase tracking-wider relative">About</a>
              <a href="#services" className="nav-link text-white/80 hover:text-white transition-all duration-300 text-sm uppercase tracking-wider relative">Services</a>
              <a href="#gallery" className="nav-link text-white/80 hover:text-white transition-all duration-300 text-sm uppercase tracking-wider relative">Gallery</a>
              <a href="#testimonials" className="nav-link text-white/80 hover:text-white transition-all duration-300 text-sm uppercase tracking-wider relative">Testimonials</a>
              <Button onClick={scrollToBooking} className="bg-white text-black hover:bg-white/90 hover:scale-105 transition-all duration-300 uppercase tracking-wider text-sm shadow-lg">
                Book Now
              </Button>
            </div>
            <button 
              className="md:hidden text-white hover:scale-110 transition-transform duration-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
            </button>
          </div>
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-6 pb-6 space-y-4 animate-slide-down">
              <a href="#about" className="block text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="#services" className="block text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#gallery" className="block text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider" onClick={() => setMobileMenuOpen(false)}>Gallery</a>
              <a href="#testimonials" className="block text-white/80 hover:text-white transition-colors text-sm uppercase tracking-wider" onClick={() => setMobileMenuOpen(false)}>Testimonials</a>
              <Button onClick={() => { scrollToBooking(); setMobileMenuOpen(false); }} className="w-full bg-white text-black hover:bg-white/90 uppercase tracking-wider text-sm">
                Book Now
              </Button>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section with Video */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0 bg-black">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{ opacity: 0.5 }}
          >
            <source src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/vs1iky44_copy_BC644361-7BF5-4CF0-B70B-521A126D768A.mov" type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
        
        {/* Video Controls */}
        <div className="absolute top-24 right-6 z-10 flex space-x-3">
          <button
            onClick={togglePlay}
            className="bg-white/10 backdrop-blur-sm p-3 rounded-full hover:bg-white/20 hover:scale-110 transition-all duration-300 shadow-lg"
          >
            {isPlaying ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white" />}
          </button>
          <button
            onClick={toggleMute}
            className="bg-white/10 backdrop-blur-sm p-3 rounded-full hover:bg-white/20 hover:scale-110 transition-all duration-300 shadow-lg"
          >
            {isMuted ? <VolumeX className="h-5 w-5 text-white" /> : <Volume2 className="h-5 w-5 text-white" />}
          </button>
        </div>

        <div className="relative h-full flex items-center justify-center text-center px-6">
          <div className="max-w-5xl" style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            opacity: 1 - scrollY / 500
          }}>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight leading-tight">
                World-Class Vocals,<br />
                Soulful Presence,<br />
                Unforgettable Experience
              </h1>
              <p className="text-xl text-white/90 mb-8 font-light">
                - J Lavaly, International Vocalist -
              </p>
            </div>
            <div className="animate-fade-in-up flex flex-wrap justify-center gap-6 mb-12" style={{ animationDelay: '0.4s' }}>
              <a href="#services" className="group text-white border-b-2 border-white/60 hover:border-white pb-2 transition-all duration-300 uppercase tracking-wider text-sm relative overflow-hidden">
                <span className="relative z-10">Weddings</span>
                <div className="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </a>
              <a href="#services" className="group text-white border-b-2 border-white/60 hover:border-white pb-2 transition-all duration-300 uppercase tracking-wider text-sm relative overflow-hidden">
                <span className="relative z-10">Corporate Events</span>
                <div className="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </a>
              <a href="#services" className="group text-white border-b-2 border-white/60 hover:border-white pb-2 transition-all duration-300 uppercase tracking-wider text-sm relative overflow-hidden">
                <span className="relative z-10">Private Functions</span>
                <div className="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </a>
              <a href="#services" className="group text-white border-b-2 border-white/60 hover:border-white pb-2 transition-all duration-300 uppercase tracking-wider text-sm relative overflow-hidden">
                <span className="relative z-10">Session Work</span>
                <div className="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </a>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <Button onClick={scrollToBooking} size="lg" className="bg-white text-black hover:bg-white/90 hover:scale-110 transition-all duration-500 uppercase tracking-wider text-lg px-12 py-6 shadow-2xl">
                Book Performance
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button 
          onClick={scrollToContent}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer hover:scale-110 transition-transform duration-300"
        >
          <ArrowDown className="h-8 w-8 text-white/80" />
        </button>
      </section>

      {/* Welcome Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="animate-on-scroll opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">Welcome</h2>
            <p className="text-xl md:text-2xl text-black/70 leading-relaxed">
              I'm J Lavaly, an international vocalist and lead singer of The Kingdom Choir. For over 15 years, I've been bringing unforgettable performances to stages worldwide. Let me bring that same energy and artistry to your event.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#B8956A] py-8">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { id: 'stat-intl', icon: Globe, label: 'International Experience', value: '10+' },
              { id: 'stat-years', icon: Star, label: 'Years Performing', value: '15+' },
              { id: 'stat-choir', icon: Award, label: 'Kingdom Choir', value: 'Lead' },
              { id: 'stat-events', icon: Mic, label: 'Events Performed', value: '500+' }
            ].map((stat, index) => (
              <div key={stat.id} className="text-center animate-on-scroll opacity-0" style={{ animationDelay: `${index * 0.1}s` }}>
                <stat.icon className="h-8 w-8 text-white mx-auto mb-2" />
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-xs text-white/90 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kingdom Choir Highlight */}
      <section className="py-32 px-6 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#B8956A] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#B8956A] rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll opacity-0" style={{ animationDelay: '0.2s' }}>
              <h5 className="text-[#B8956A] uppercase tracking-wider text-sm mb-6 font-semibold">Career Highlight</h5>
              <h2 className="text-6xl font-bold text-white mb-8 leading-tight">The Kingdom Choir</h2>
              <p className="text-white/70 text-xl leading-relaxed mb-8">
                Lead singer of the world-renowned Kingdom Choir, bringing soul, authority, and unforgettable moments to stages worldwide.
              </p>
              <div className="space-y-4">
                {[
                  { id: 'highlight-intl', icon: Star, text: 'International Experience' },
                  { id: 'highlight-countries', icon: Globe, text: 'Multiple Countries & Cultures' },
                  { id: 'highlight-performer', icon: Award, text: 'World-Class Performer' }
                ].map((item) => (
                  <div key={item.id} className="flex items-center space-x-4 group">
                    <div className="bg-[#B8956A]/20 p-3 rounded-lg group-hover:bg-[#B8956A]/30 transition-all duration-300">
                      <item.icon className="h-6 w-6 text-[#B8956A]" />
                    </div>
                    <span className="text-white/80 text-lg group-hover:text-white transition-colors duration-300">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative animate-on-scroll opacity-0" style={{ animationDelay: '0.4s' }}>
              <div className="absolute -inset-4 bg-gradient-to-r from-[#B8956A]/20 to-transparent rounded-2xl filter blur-2xl"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/7jzbedar_F6A6E6AC-9FC9-4987-AEB8-20988134E54C.jpeg"
                alt="Kingdom Choir Performance"
                className="relative rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12">
            <h5 className="text-[#B8956A] uppercase tracking-wider text-sm mb-4 font-semibold text-center">My Story</h5>
            <h2 className="text-5xl font-bold text-black mb-8 text-center">About J Lavaly</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-on-scroll opacity-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/y7oo70ax_IMG_0268.jpeg" 
                alt="J Lavaly" 
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
            <div className="space-y-6 text-black/70 text-lg leading-relaxed animate-on-scroll opacity-0" style={{ animationDelay: '0.2s' }}>
              <p>
                J Lavaly is an internationally experienced vocalist and performer known for commanding stages, reading rooms, and creating unforgettable live moments. With a voice that carries both soul and authority, J Lavaly brings a rare balance of artistry and professionalism to every performance — whether in intimate settings or on large, global platforms.
              </p>
              <p>
                Having performed across multiple countries and cultures, J Lavaly has built a reputation for versatility. From elegant weddings and high-end corporate and senior functions, to vibrant hotel residencies and live bar performances, his ability to adapt his sound, presence, and energy makes him a trusted choice for diverse audiences and events.
              </p>
              <p>
                More than just a singer, J Lavaly understands atmosphere. His performances are rooted in connection — connecting with the crowd, the occasion, and the emotion of the moment. This intentional approach has led to repeat bookings, international opportunities, and recognition as a reliable, world-class live performer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 px-6 bg-[#f8f8f8] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#B8956A] rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-20 animate-on-scroll opacity-0">
            <h5 className="text-[#B8956A] uppercase tracking-wider text-sm mb-6 font-semibold">What I Offer</h5>
            <h2 className="text-6xl md:text-7xl font-bold text-black mb-8">Services</h2>
            <p className="text-black/60 text-xl">Professional entertainment for every occasion</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div key={service.id} className="group animate-on-scroll opacity-0" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="bg-white p-10 h-full hover:shadow-2xl transition-all duration-500 border border-black/5 hover:border-[#B8956A]/20 hover:-translate-y-2 relative overflow-hidden">
                    {service.image && (
                      <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-500">
                        <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#B8956A]/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
                    <div className="relative z-10">
                      <div className="mb-8">
                        <h5 className="text-[#B8956A] uppercase tracking-wider text-xs mb-4 font-semibold">Service</h5>
                        <h3 className="text-2xl font-bold text-black mb-4 group-hover:text-[#B8956A] transition-colors duration-300">{service.title}</h3>
                      </div>
                      <p className="text-black/60 leading-relaxed mb-8 group-hover:text-black/80 transition-colors duration-300">{service.description}</p>
                      <div className="pt-6 border-t border-black/10">
                        <IconComponent className="h-8 w-8 text-[#B8956A] group-hover:scale-110 transition-transform duration-300" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-32 px-6 bg-black relative overflow-hidden">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-20 animate-on-scroll opacity-0">
            <h5 className="text-[#B8956A] uppercase tracking-wider text-sm mb-6 font-semibold">Performances</h5>
            <h2 className="text-6xl md:text-7xl font-bold text-white mb-8">Gallery</h2>
            <p className="text-white/60 text-xl">A glimpse into world-class performances</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {[
              { id: 'gallery-live-stage', src: 'https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/s2oqa12h_IMG_2982.jpeg', title: 'Live Stage', subtitle: 'Concert Performance', type: 'image' },
              { id: 'gallery-solo', src: 'https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/v4ktldnp_IMG_8339.jpeg', title: 'Solo Performance', subtitle: 'Intimate Setting', type: 'image' },
              { id: 'gallery-concert', src: 'https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/lhrws2r9_IMG_9047.jpeg', title: 'Live Concert', subtitle: 'International Stage', type: 'image' },
              { id: 'gallery-studio', src: 'https://customer-assets-jt897jd0.emergentagent.net/job_57d4f2bd-1fcc-479b-8b0a-9be94ef8d753/artifacts/2c4dc1e790021a79_84A5DBFC-75CE-4CF1-AA7C-3E1A7E3FE947.webp', title: 'Studio Sessions', subtitle: 'Recording & Session Work', type: 'image' }
            ].map((item, index) => (
              <div key={item.id} className="relative overflow-hidden group h-[500px] animate-on-scroll opacity-0" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#B8956A]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"></div>
                {item.type === 'image' ? (
                  <img 
                    src={item.src}
                    alt={item.title}
                    className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-110 group-hover:rotate-1"
                  />
                ) : (
                  <video 
                    src={item.src}
                    className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-105"
                    loop
                    muted
                    playsInline
                    onMouseEnter={(e) => e.target.play()}
                    onMouseLeave={(e) => e.target.pause()}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  <h5 className="text-[#B8956A] text-sm uppercase tracking-wider mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">Performance</h5>
                  <h3 className="text-white text-3xl font-bold mb-2">{item.title}</h3>
                  <p className="text-white/80 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">{item.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial/Quote Section */}
      <section className="py-32 px-6 bg-[#B8956A] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <Music className="h-96 w-96 absolute top-10 left-10 rotate-12" />
            <Music className="h-64 w-64 absolute bottom-10 right-10 -rotate-12" />
          </div>
        </div>
        <div className="container mx-auto max-w-4xl text-center relative z-10 animate-on-scroll opacity-0">
          <blockquote className="text-4xl md:text-5xl font-bold text-white leading-relaxed mb-8">
            "Every performance is treated as a reflection of my journey — professional, authentic, and impactful."
          </blockquote>
          <p className="text-white/90 text-xl">— J Lavaly</p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-32 px-6 bg-[#0a0a0a] relative overflow-hidden">
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-20 animate-on-scroll opacity-0">
            <p className="text-[#B8956A] uppercase tracking-[0.3em] text-sm mb-4">Kind Words</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">Testimonials</h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">What clients say about working with J Lavaly</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allTestimonials.map((t, i) => (
              <div
                key={t.id}
                data-testid={`testimonial-card-${t.id}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:-translate-y-2 transition-all duration-500 animate-on-scroll opacity-0"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <Quote className="h-10 w-10 text-[#B8956A] mb-6" />
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className={`h-4 w-4 ${s < (t.rating || 5) ? 'fill-[#B8956A] text-[#B8956A]' : 'text-white/20'}`} />
                  ))}
                </div>
                <p className="text-white/80 leading-relaxed mb-8 italic">"{t.quote}"</p>
                <div className="flex items-center gap-4 pt-6 border-t border-white/10">
                  <div className="w-12 h-12 rounded-full bg-[#B8956A]/20 flex items-center justify-center text-[#B8956A] font-bold text-lg">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{t.name}</p>
                    <p className="text-white/50 text-sm">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16 animate-on-scroll opacity-0">
            <p className="text-white/60 mb-6">Worked with J Lavaly? We'd love to hear from you.</p>
            <Button
              onClick={() => setReviewOpen(true)}
              data-testid="leave-review-btn-testimonials"
              className="bg-[#B8956A] hover:bg-[#9a7a52] text-white uppercase tracking-wider px-10 py-6 hover:scale-105 transition-all duration-300"
            >
              Leave a Review
            </Button>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-32 px-6 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#B8956A] rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto max-w-3xl relative z-10">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h5 className="text-[#B8956A] uppercase tracking-wider text-sm mb-6 font-semibold">Get In Touch</h5>
            <h2 className="text-6xl md:text-7xl font-bold text-white mb-8">Book J Lavaly</h2>
            <p className="text-white/70 text-xl">
              Ready to book? I'd love to be part of your special event.
            </p>
          </div>

          {submitted && (
            <div data-testid="booking-success-message" className="mb-10 bg-[#B8956A]/15 border border-[#B8956A]/40 rounded-2xl p-6 md:p-8 flex items-start gap-4 animate-fade-in">
              <CheckCircle2 className="h-8 w-8 text-[#B8956A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-white text-xl md:text-2xl font-bold mb-2">Your inquiry has been sent!</h3>
                <p className="text-white/70 leading-relaxed">
                  Thank you for reaching out. I've received your booking inquiry and will get back to you within 24 hours. A confirmation email is on its way to your inbox.
                </p>
                <Button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="send-another-inquiry-btn"
                  className="mt-4 bg-white text-black hover:bg-white/90 transition-all duration-300 uppercase tracking-wider text-xs px-6 py-2"
                >
                  Send another inquiry
                </Button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 animate-on-scroll opacity-0" style={{ animationDelay: '0.2s' }}>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative group">
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  minLength={2}
                  maxLength={100}
                  data-testid="booking-name-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] h-14 text-lg transition-all duration-300 group-hover:bg-white/10"
                  placeholder="Full Name *"
                />
              </div>
              <div className="relative group">
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  data-testid="booking-email-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] h-14 text-lg transition-all duration-300 group-hover:bg-white/10"
                  placeholder="Email Address *"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative group">
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  minLength={10}
                  data-testid="booking-phone-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] h-14 text-lg transition-all duration-300 group-hover:bg-white/10"
                  placeholder="Phone Number *"
                />
              </div>
              <div className="relative group">
                <Input
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleInputChange}
                  required
                  data-testid="booking-event-type-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] h-14 text-lg transition-all duration-300 group-hover:bg-white/10"
                  placeholder="Event Type *"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative group">
                <label className="block text-white/50 text-xs uppercase tracking-wider mb-2 pl-1">Event Date *</label>
                <Input
                  name="eventDate"
                  type="date"
                  value={formData.eventDate}
                  onChange={handleInputChange}
                  required
                  data-testid="booking-event-date-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] h-14 text-lg transition-all duration-300 group-hover:bg-white/10"
                />
              </div>
              <div className="relative group">
                <label className="block text-white/50 text-xs uppercase tracking-wider mb-2 pl-1">Venue Postcode *</label>
                <Input
                  name="venuePostcode"
                  value={formData.venuePostcode}
                  onChange={handleInputChange}
                  required
                  data-testid="booking-venue-postcode-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] h-14 text-lg transition-all duration-300 group-hover:bg-white/10"
                  placeholder="e.g. SW1A 1AA"
                />
              </div>
            </div>
            <div className="relative group">
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                minLength={10}
                maxLength={1000}
                rows={6}
                data-testid="booking-message-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#B8956A] focus:ring-[#B8956A] text-lg transition-all duration-300 group-hover:bg-white/10"
                placeholder="Tell us about your event *"
              />
            </div>
            <Button type="submit" size="lg" disabled={isSubmitting} data-testid="booking-submit-btn" className="w-full bg-white text-black hover:bg-white/90 hover:scale-105 transition-all duration-500 uppercase tracking-wider text-lg py-8 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed">
              <Send className="mr-3 h-6 w-6" />
              {isSubmitting ? 'Sending...' : 'Send Inquiry'}
            </Button>
          </form>

          {/* Travel & Location Fees */}
          <div data-testid="travel-fees-section" className="mt-16 bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10 animate-on-scroll opacity-0" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#B8956A]/20 p-3 rounded-lg">
                <MapPin className="h-6 w-6 text-[#B8956A]" />
              </div>
              <div>
                <h5 className="text-[#B8956A] uppercase tracking-wider text-xs font-semibold">Good to Know</h5>
                <h3 className="text-2xl md:text-3xl font-bold text-white">Travel &amp; Location Fees</h3>
              </div>
            </div>
            <div className="space-y-4 text-white/70 leading-relaxed text-base">
              <p>Our standard booking packages apply to events within our usual service area in Greater London.</p>
              <p>Bookings outside this area may incur an additional Travel &amp; Location Fee, calculated according to the venue's distance from London, journey time and any additional travel expenses.</p>
              <p>For bookings involving significant travel, additional costs may include mileage, parking, tolls and overnight accommodation where required.</p>
              <p>All applicable travel and location fees will be confirmed in your personalised quotation before your booking is secured.</p>
              <p>For locations requiring extensive travel or an overnight stay, a bespoke quotation will be provided to ensure the necessary arrangements can be made for your event.</p>
              <p className="text-white/90 font-medium">We are happy to travel across the UK to help make your occasion special. Please provide your venue postcode when enquiring so we can confirm the full cost of your booking.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Join the Lav Fam - Newsletter Section */}
      <section className="py-12 px-6 bg-[#B8956A]/10 border-y border-[#B8956A]/20">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-black mb-2">Join the Lav Fam!</h3>
              <p className="text-black/60 text-sm md:text-base">Subscribe for exclusive news updates</p>
            </div>
            <a 
              href="https://beacons.ai/jlavaly" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-black text-white hover:bg-black/80 hover:scale-105 transition-all duration-300 uppercase tracking-wider text-sm px-8 py-4 inline-flex items-center gap-2 shadow-lg"
            >
              <Mail className="h-4 w-4" />
              Subscribe Now
            </a>
          </div>
        </div>
      </section>

      {/* As Seen On Section */}
      <section className="py-20 px-6 bg-white border-t border-black/10">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-on-scroll opacity-0">
            <h5 className="text-[#B8956A] uppercase tracking-wider text-sm mb-4 font-semibold">Trusted By</h5>
            <h2 className="text-5xl md:text-6xl font-bold text-black mb-4">As Seen On</h2>
            <p className="text-black/60 text-lg max-w-2xl mx-auto">
              Proud to have performed with world-renowned brands, networks, and prestigious events
            </p>
          </div>
          
          {/* Logo Carousel */}
          <div className="relative overflow-hidden">
            <div className="logo-slider">
              <div className="logo-track">
                {/* First set of logos */}
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/fjru0dw5_IMG_4187.png" alt="BBC" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/zdseyc7x_IMG_4188.png" alt="ITV" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg" alt="Netflix" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/j5dhtjfh_IMG_4191.png" alt="Burberry" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/vkdl2luf_IMG_4193.png" alt="Cartier" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/m9oej47l_IMG_4189.png" alt="Converse" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/b7tc7h5s_IMG_4159.png" alt="The Voice UK" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/4buqxta0_IMG_4160.png" alt="MOBO Awards" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/29mz0xno_IMG_4163.png" alt="London Fashion Week" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/2r7mu408_IMG_4164.png" alt="SXSW" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/xd6opbua_IMG_4165.png" alt="Dubai Opera" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/7qmhvg1j_IMG_4166.png" alt="Expo Dubai" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/u0d5jsne_IMG_4167.png" alt="Cunard" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/ybqlvr2c_IMG_4169.png" alt="The Lateish Show with Mo Gilligan" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/3n6v7x77_IMG_4168.png" alt="BBC" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/ck14uv42_IMG_4170.png" alt="Annabel's" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/q69ifcsh_IMG_4171.png" alt="Capital Xtra" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/77ixqy95_IMG_4205.png" alt="GUAP" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/2fjw5kem_IMG_4173.png" alt="Monte Carlo Gala" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/a0wtawnx_IMG_4174.png" alt="Capital Xtra" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/dsd4iail_IMG_4175.png" alt="Sofar Sounds" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/pxptv0xt_IMG_4176.png" alt="Concert for Ukraine" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/65s7puw6_IMG_4178.png" alt="David's Tent" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/mf8acwr9_IMG_4179.png" alt="Chris Harper Productions" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/cbescn1f_IMG_4180.png" alt="The Kingdom Choir" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/0qtstoc3_IMG_4208.png" alt="BBTA" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/v768p9rf_IMG_4210.png" alt="Coronation Proms" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_8544aaa2-db0a-4380-9e86-0585c598d3c1/artifacts/ppdmlcy0_IMG_4212.png" alt="Coca-Cola" className="logo-img" />
                </div>
                
                {/* Duplicate set for infinite scroll */}
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/fjru0dw5_IMG_4187.png" alt="BBC" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/zdseyc7x_IMG_4188.png" alt="ITV" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg" alt="Netflix" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/j5dhtjfh_IMG_4191.png" alt="Burberry" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/vkdl2luf_IMG_4193.png" alt="Cartier" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/m9oej47l_IMG_4189.png" alt="Converse" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/b7tc7h5s_IMG_4159.png" alt="The Voice UK" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/4buqxta0_IMG_4160.png" alt="MOBO Awards" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/29mz0xno_IMG_4163.png" alt="London Fashion Week" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/2r7mu408_IMG_4164.png" alt="SXSW" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/xd6opbua_IMG_4165.png" alt="Dubai Opera" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/7qmhvg1j_IMG_4166.png" alt="Expo Dubai" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/u0d5jsne_IMG_4167.png" alt="Cunard" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/ybqlvr2c_IMG_4169.png" alt="The Lateish Show with Mo Gilligan" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/3n6v7x77_IMG_4168.png" alt="BBC" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/ck14uv42_IMG_4170.png" alt="Annabel's" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/q69ifcsh_IMG_4171.png" alt="Capital Xtra" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/77ixqy95_IMG_4205.png" alt="GUAP" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/2fjw5kem_IMG_4173.png" alt="Monte Carlo Gala" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/a0wtawnx_IMG_4174.png" alt="Capital Xtra" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/dsd4iail_IMG_4175.png" alt="Sofar Sounds" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/pxptv0xt_IMG_4176.png" alt="Concert for Ukraine" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/65s7puw6_IMG_4178.png" alt="David's Tent" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/mf8acwr9_IMG_4179.png" alt="Chris Harper Productions" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/cbescn1f_IMG_4180.png" alt="The Kingdom Choir" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/0qtstoc3_IMG_4208.png" alt="BBC Radio 2" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/v768p9rf_IMG_4210.png" alt="Songs of Praise" className="logo-img" />
                </div>
                <div className="logo-item">
                  <img src="https://customer-assets.emergentagent.com/job_8544aaa2-db0a-4380-9e86-0585c598d3c1/artifacts/ppdmlcy0_IMG_4212.png" alt="Coca-Cola" className="logo-img" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Additional context text */}
          <div className="mt-12 text-center">
            <p className="text-black/60 text-sm">
              Performed at prestigious venues and events across the UK, Europe, UAE, USA and Africa
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-16 px-6 border-t border-white/10">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <a href="/" className="inline-block mb-6 hover:opacity-80 transition-opacity duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_kingdom-vocalist/artifacts/0n13s63z_IMG_7492.png" 
                  alt="J Lavaly Music" 
                  className="h-20 w-auto"
                />
              </a>
              <p className="text-white/60 text-lg leading-relaxed">
                International vocalist bringing soul and authority to stages worldwide. Every performance is a reflection of artistry, professionalism, and unforgettable moments.
              </p>
              <p className="text-white/60 text-sm mt-4">
                Visit us at <a href="https://jlavalymusic.com" className="text-[#B8956A] hover:text-white transition-colors">JLAVALYmusic.com</a>
              </p>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider mb-6 font-semibold text-[#B8956A]">Quick Links</h3>
              <ul className="space-y-3">
                <li><a href="#about" className="text-white/60 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transform duration-300">About</a></li>
                <li><a href="#services" className="text-white/60 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transform duration-300">Services</a></li>
                <li><a href="#gallery" className="text-white/60 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transform duration-300">Gallery</a></li>
                <li><a href="#testimonials" className="text-white/60 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transform duration-300">Testimonials</a></li>
                <li><button type="button" onClick={() => setReviewOpen(true)} data-testid="leave-review-btn-footer" className="text-white/60 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transform duration-300 text-left">Leave a Review</button></li>
                <li><a href="#booking" className="text-white/60 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transform duration-300">Book Now</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider mb-6 font-semibold text-[#B8956A]">Contact</h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3 text-white/60 text-sm group">
                  <Mail className="h-4 w-4 group-hover:text-[#B8956A] transition-colors duration-300" />
                  <span className="group-hover:text-white transition-colors duration-300">info@jlavalymusic.com</span>
                </li>
                <li className="flex items-center space-x-3 text-white/60 text-sm group">
                  <MapPin className="h-4 w-4 group-hover:text-[#B8956A] transition-colors duration-300" />
                  <span className="group-hover:text-white transition-colors duration-300">International</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} J Lavaly. All rights reserved. | World-Class Entertainment
            </p>
          </div>
        </div>
      </footer>
      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} />
    </div>
  );
};